import { BondRepaymentMonthlyResult, LumpSumComparison } from '../types/calculator.types';

/**
 * Calculate monthly bond repayment for different terms
 */
export const calculateMonthlyRepayments = (
  loanAmount: number,
  depositAmount: number,
  annualInterestRate: number
): BondRepaymentMonthlyResult[] => {
  const totalLoanAmount = loanAmount - depositAmount;
  const monthlyInterestRate = annualInterestRate / 100 / 12;

  const terms = [10, 15, 20, 25, 30]; // years

  return terms.map((termYears) => {
    const months = termYears * 12;
    const monthlyPayment =
      (monthlyInterestRate * totalLoanAmount) /
      (1 - Math.pow(1 + monthlyInterestRate, -months));
    const totalAmount = monthlyPayment * months;
    const totalInterest = totalAmount - totalLoanAmount;

    return {
      term: termYears,
      monthlyPayment,
      totalInterest,
      totalAmount,
    };
  });
};

/**
 * Calculate lump sum payment impact
 */
export const calculateLumpSumImpact = (
  loanAmount: number,
  annualInterestRate: number,
  loanTermYears: number,
  lumpSumAmount: number
): LumpSumComparison | null => {
  if (lumpSumAmount >= loanAmount) {
    return null;
  }

  const monthlyInterestRate = annualInterestRate / 100 / 12;

  // Original loan calculations
  const originalMonthlyPayment =
    (monthlyInterestRate * loanAmount) /
    (1 - Math.pow(1 + monthlyInterestRate, -loanTermYears * 12));
  const originalTotalAmountPaid = originalMonthlyPayment * (loanTermYears * 12);
  const originalTotalInterestPaid = originalTotalAmountPaid - loanAmount;

  // New term after lump sum
  const newTermYears =
    Math.log(
      1 - ((loanAmount - lumpSumAmount) * monthlyInterestRate) / originalMonthlyPayment
    ) /
    Math.log(1 + monthlyInterestRate) /
    -12;

  const years = Math.floor(newTermYears);
  const months = Math.round((newTermYears - years) * 12);
  const totalLumpMonths = years * 12 + months;

  const lumpTotalInterestPaid =
    totalLumpMonths * originalMonthlyPayment + lumpSumAmount - loanAmount;
  const lumpTotalAmountPaid = totalLumpMonths * originalMonthlyPayment + lumpSumAmount;

  const interestSaved = originalTotalInterestPaid - lumpTotalInterestPaid;
  const durationSaved = loanTermYears - newTermYears;
  const timeSavedYears = Math.floor(durationSaved);
  const timeSavedMonths = Math.round((durationSaved - timeSavedYears) * 12);

  return {
    originalPayOffTime: loanTermYears,
    originalMonthlyPayment,
    originalTotalCapitalPaid: loanAmount,
    originalTotalInterestPaid,
    originalTotalAmountPaid,
    lumpPayOffTime: `${years} years ${months} months`,
    lumpMonthlyPayment: originalMonthlyPayment,
    lumpTotalCapitalPaid: loanAmount,
    lumpTotalInterestPaid,
    lumpTotalAmountPaid,
    interestSaved,
    timeSaved: `${timeSavedYears} years ${timeSavedMonths} months`,
  };
};

/**
 * Calculate agent commission on property price
 */
export const calculateAgentCommission = (
  propertyPrice: number,
  commissionRate: number,
  vatRate: number
): { commission: number; vatOnCommission: number; grossPriceLess: number; grossPricePlus: number } => {
  const commission = (commissionRate / 100) * propertyPrice;
  const vatOnCommission = (vatRate / 100) * commission;
  const grossPriceLess = propertyPrice - commission - vatOnCommission;
  const grossPricePlus = propertyPrice + commission + vatOnCommission;

  return {
    commission,
    vatOnCommission,
    grossPriceLess,
    grossPricePlus,
  };
};

/**
 * Calculate estate agent split
 */
export const calculateAgentSplit = (
  propertyPrice: number,
  estateAgencyRate: number,
  agentRate: number,
  vatRate: number
): {
  totalCommissionToAgency: number;
  vatOnCommission: number;
  commissionToAgent: number;
  commissionToAgency: number;
  nettToClient: number;
} => {
  const totalCommissionToAgency = (estateAgencyRate / 100) * propertyPrice;
  const vatOnCommission = (vatRate / 100) * totalCommissionToAgency;
  const commissionToAgent = (agentRate / 100) * totalCommissionToAgency;
  const commissionToAgency = totalCommissionToAgency - commissionToAgent;
  const nettToClient = propertyPrice - totalCommissionToAgency - vatOnCommission;

  return {
    totalCommissionToAgency,
    vatOnCommission,
    commissionToAgent,
    commissionToAgency,
    nettToClient,
  };
};

/**
 * Calculate cost per square meter
 */
export const calculateCostPerSqm = (sellingPrice: number, sizeInSqm: number): number => {
  return sellingPrice / sizeInSqm;
};
